// Input contract — Detection Engine System 1 (spec §1).
//
// Единственный числовой вход движка: step6.stage[i].L1..L7, −3..+3, каждый с
// boolean `touched` (s164). Непотроганный (default 0) НЕ участвует в
// интерпретации; знаменатель «X of seven» = touched-count.
//
// NB нумерация: «step6» = ЛЕГАСИ wizard-s-id (deep-dive слайдеры). Канон = сквозная
// 1–7 (s449): это теперь шаг 3 (deck). Движок numbering-agnostic — берёт слайдеры
// независимо от собравшего экрана; ключи контракта не меняются.
//
// v1-упрощение: touched трактуется на уровне СЛОЯ (слой активен, если тронут
// хотя бы на одном этапе), не per-stage-per-layer. Этого достаточно, чтобы
// невыставленные дефолтные 0 не создавали ложных parallel/singleton; per-stage
// touched-маска — резерв v2. Фикстуры/bench-29 = все 7 активны.

import { LAYERS, CODE_TO_LAYER } from './layers.js';

/**
 * Нормализует разные формы wizard-входа к каноническому массиву этапов.
 *
 * Принимает:
 *   - [{ Vitality: 2, Security: -1, ... }]            (все слои touched)
 *   - [{ L1: 2, L2: -1, ... }]                        (L#-коды → имена)
 *   - [{ values: {...}, touched: {...} }]             (явная маска)
 *
 * Возвращает: { stages: [{ values, touched }], active: [layerName] }
 *   values  — { layerName: number } (все 7, дефолт 0)
 *   touched — { layerName: boolean }
 *   active  — список слоёв, активных хотя бы на одном этапе (touched-count base)
 */
export function normalizeStages(input) {
  const raw = Array.isArray(input) ? input : input?.stages;
  if (!Array.isArray(raw) || raw.length === 0) {
    throw new Error('normalizeStages: expected a non-empty array of stages');
  }

  const stages = raw.map((stage) => {
    const values = {};
    const touched = {};
    const hasExplicitMask =
      stage && typeof stage === 'object' && 'values' in stage;
    const vSrc = hasExplicitMask ? stage.values || {} : stage;
    const tSrc = hasExplicitMask ? stage.touched || {} : null;

    for (const L of LAYERS) {
      const v = readLayer(vSrc, L);
      values[L] = v == null ? 0 : v;
      if (tSrc) {
        touched[L] = Boolean(readLayer(tSrc, L));
      } else {
        // Без явной маски: слой touched, если значение присутствует во входе.
        touched[L] = v != null;
      }
    }
    return { values, touched };
  });

  const active = LAYERS.filter((L) => stages.some((s) => s.touched[L]));
  return { stages, active };
}

/** Читает значение слоя по имени ИЛИ по L#-коду. */
function readLayer(src, layerName) {
  if (src == null) return null;
  if (layerName in src && src[layerName] != null) return num(src[layerName]);
  const code = Object.keys(CODE_TO_LAYER).find(
    (c) => CODE_TO_LAYER[c] === layerName,
  );
  if (code && code in src && src[code] != null) return num(src[code]);
  return null;
}

function num(x) {
  if (typeof x === 'boolean') return x ? 1 : 0;
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}

/** Массив значений слоя L по этапам. */
export function series(stages, L) {
  return stages.map((s) => s.values[L]);
}

/**
 * Слой L тронут НА этапе `s`. Robust: отсутствующая маска (raw-фикстуры, bench-29)
 * ⇒ тронут — так all-touched вход ведёт себя ровно как до Фазы 2 (byte-parity).
 */
export const isTouchedAt = (s, L) => !s.touched || !!s.touched[L];

/** Индексы этапов, где L тронут (per-stage touched-mask, s443.d5 Фаза 2). */
export function touchedStagesOf(stages, L) {
  const out = [];
  for (let i = 0; i < stages.length; i++) if (isTouchedAt(stages[i], L)) out.push(i);
  return out;
}

/**
 * Ряд L по этапам, carrier-facing: нетронутый-на-этапе → `null` (не 0), spec §2.1.
 * На all-touched === series() (нет null) → несущая идентична.
 */
export function seriesTouched(stages, L) {
  return stages.map((s) => (isTouchedAt(s, L) ? s.values[L] : null));
}

/**
 * Среднее ДРУГИХ слоёв, ТРОНУТЫХ на этапе idx (touched-per-stage, spec §3.1 / s443.d5
 * Фаза 2). Раньше усредняло по всему `active` — нетронутый-на-этапе слой втягивался как
 * выставленный 0 и раздувал/сжимал поле, фабрикуя ложный gap. Теперь знаменатель = число
 * тронутых-на-этапе других слоёв. На all-touched === прежняя формула (byte-parity).
 * Ни одного тронутого другого слоя на этапе → 0 (fallback как прежде).
 */
export function othersMeanAt(stages, active, L, idx) {
  const others = active.filter((o) => o !== L && isTouchedAt(stages[idx], o));
  if (others.length === 0) return 0;
  const sum = others.reduce((acc, o) => acc + stages[idx].values[o], 0);
  return sum / others.length;
}

/** gap(L,i) = L(i) − mean(other active @ i). Знаковый. */
export function gapAt(stages, active, L, idx) {
  return stages[idx].values[L] - othersMeanAt(stages, active, L, idx);
}

export const mean = (arr) => arr.reduce((a, b) => a + b, 0) / arr.length;

/**
 * Силуэт (растущая линия §I/§II/§VII + анкер forward-проекции §2.8) — среднее по
 * слоям, тронутым НА ЭТОМ ЭТАПЕ (touched-per-stage, s443.d5 Фаза 1). Раньше усреднял
 * по `active` (слой-уровень: тронут хотя бы на одном этапе), из-за чего нетронутый на
 * этапе слайдер втягивался как выставленный 0. Теперь нетронутый-на-этапе слой выпадает
 * из силуэта, а не читается как 0.
 *
 * Byte-parity: на all-touched входе (bench-29 / фикстуры — все 7 тронуты на всех этапах)
 * per-stage touched === active → результат идентичен, снапшоты не двигаются.
 *
 * НЕ трогает несущую/детекторы §3.1 (те остаются на `active` — это Фаза 2). Этап без
 * единого тронутого слоя → `null`, НЕ 0 (s836.d3). Ноль фабриковал данные, которых человек
 * не давал; читатель, которому нужно число, зовёт `filled()` локально и назад не пишет.
 * Недостижимо за полом деки s3 (≥2 тронутых на этап, s495.d1) — защитная ветка.
 */
export function silhouette(stages, active) {
  return stages.map((s) => {
    const touchedHere = active.filter((L) => s.touched[L]);
    return touchedHere.length ? mean(touchedHere.map((L) => s.values[L])) : null;
  });
}

/**
 * filled() — интерполяция дырок силуэта ДЛЯ ЧИТАТЕЛЯ (E2, s493; тот же приём, что `fillCarrier`
 * расширил на §IV-несущую, board canon `s5 Red Line v6`). Пустой этап силуэта = `null` (s836.d3);
 * null НЕ кладётся в паспорт/вид — читателю, которому нужно число, отдаём ФОРМУ, не утверждение:
 *   · внутренний null → линейная интерполяция соседей
 *   · хвостовой null  → последнее известное, плоско вправо
 *   · головной null   → первое известное, плоско влево
 * Заполненный массив НИКОГДА не покидает читателя (иначе форма станет утверждением, s4-handoff п.4).
 * Полностью пустой ряд (или не-массив) → возвращается как есть; вызывающий с `?? 0` это ловит.
 */
export function filled(c) {
  if (!Array.isArray(c)) return c;
  if (!c.some((x) => x != null)) return c.slice();
  const out = c.slice();
  let prev = null;
  for (let i = 0; i < out.length; i++) {
    if (out[i] != null) { prev = i; continue; }
    let nx = null;
    for (let j = i + 1; j < out.length; j++) if (c[j] != null) { nx = j; break; }
    if (prev === null) out[i] = c[nx];
    else if (nx === null) out[i] = c[prev];
    else out[i] = c[prev] + (c[nx] - c[prev]) * ((i - prev) / (nx - prev));
  }
  return out;
}
